<template>
    <div class="committees-editor">
        <!-- Empty State -->
        <div v-if="!modelValue || modelValue.length === 0" class="empty-state congress-schedule-empty-state">
            <p class="mb-4">
                {{ lang('congresses::congresses.committees.empty') }}
            </p>
            <Button type="button" size="sm" color="brand" variant="inline" @click="addCommittee">
                <span class="action-icon">
                    <Icon icon="phosphor/regular/users-three"></Icon>
                </span>
                <span class="action-label-text">
                    {{ lang('congresses::congresses.committees.add_first') }}
                </span>
            </Button>
        </div>

        <!-- Committees List -->
        <div v-else class="committees-list">
            <Sortable :list="modelValue" item-key="_id" tag="div" @end="onCommitteesReordered">
                <template #item="{ element: committee, index }">
                    <div class="forms__field_input--congressSchedule-day-item">
                        <!-- Drag handle -->
                        <div class="forms__field_input--congressSchedule-day-handle">
                            <icon
                                icon="hero/solid/ellipsis-vertical"
                                class="forms__field_input--congressSchedule-day-icon cursor-grab"
                            ></icon>
                        </div>
                        <!-- Committee component -->
                        <div class="forms__field_input--congressSchedule-day-content">
                            <CommitteeCard
                                :key="committee._id"
                                v-model="modelValue[index]"
                                :committee-index="index"
                                @remove="removeCommittee(index)"
                            />
                        </div>
                    </div>
                </template>
            </Sortable>

            <!-- Add Committee Button -->
            <div class="congress-schedule-after-days-actions">
                <Button type="button" size="sm" color="brand" variant="inline" @click="addCommittee">
                    <span class="action-icon">
                        <Icon icon="phosphor/regular/users-three"></Icon>
                    </span>
                    <span class="action-label-text">
                        {{ lang('congresses::congresses.committees.add') }}
                    </span>
                </Button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
defineOptions({
    inheritAttrs: false,
});

import { v4 as uuidv4 } from 'uuid';
import { lang } from '@/lang/composable';
import Sortable from '@src/Common/Components/Sortable.vue';
import Button from '@src/Common/Components/Button.vue';
import CommitteeCard from './CommitteeCard.vue';
import type { Committee } from '../types';

const modelValue = defineModel<Committee[]>();

// Ensure all items have _id for Vue key tracking
const ensureIds = (committees: Committee[]) => {
    return committees.map((committee) => ({
        ...committee,
        _id: committee._id || uuidv4(),
        members: (committee.members || []).map((member) => ({
            ...member,
            _id: member._id || uuidv4(),
        })),
    }));
};

if (!modelValue.value) {
    modelValue.value = [];
} else {
    modelValue.value = ensureIds(modelValue.value);
}

const addCommittee = () => {
    if (!modelValue.value) {
        modelValue.value = [];
    }
    modelValue.value = [
        ...modelValue.value,
        {
            _id: uuidv4(),
            name: '',
            display_style: 'default',
            members: [],
            sort_order: modelValue.value.length,
        },
    ];
};

const removeCommittee = (index: number) => {
    if (confirm(lang('congresses::congresses.committees.confirm_remove'))) {
        modelValue.value = modelValue.value.filter((_, idx) => idx !== index);
    }
};

const onCommitteesReordered = (event: { oldIndex: number; newIndex: number }) => {
    const items = [...modelValue.value];
    const [movedItem] = items.splice(event.oldIndex, 1);
    items.splice(event.newIndex, 0, movedItem);

    items.forEach((committee, idx) => {
        committee.sort_order = idx;
    });

    modelValue.value = items;
};
</script>
